import React from 'react';
import { useTranslations } from 'src/contexts/TranslationContext';
import CommonPage from 'src/components/CommonPage';

interface TermsProps {
  lang?: string;
}

const Terms: React.FC<TermsProps> = ({ lang }) => {
  const { translations: t, language, isLoading } = useTranslations();
  const [hasTranslations, setHasTranslations] = React.useState(false);
  const [attemptCount, setAttemptCount] = React.useState(0);
  
  // Check if translations are available
  React.useEffect(() => {
    // Check both root-level and navigations-level terms data
    const termsData = t?.terms || t?.navigations?.terms;
    const hasTermsTranslations = !!termsData;
    
    setHasTranslations(hasTermsTranslations);
    
    // Log detailed information about translations for debugging
    console.log('Terms Component - Available translations:', {
      terms: t?.terms ? 'Available at root' : 'Not at root',
      navigationsTerms: t?.navigations?.terms ? 'Available in navigations' : 'Not in navigations',
      language: language,
      isLoading: isLoading,
      hasTranslations: hasTermsTranslations,
      translationsObject: t ? 'Exists' : 'Not exists',
      rootKeys: t ? Object.keys(t) : [],
      attemptCount
    });
    
    if (t?.terms) {
      console.log('Root terms structure:', Object.keys(t.terms));
    }
    
    if (t?.navigations?.terms) {
      console.log('Navigations terms structure:', Object.keys(t.navigations.terms));
    }
    
    // If translations are loaded but we don't detect terms data,
    // increment attempt counter to prevent infinite loading
    // but ONLY if we haven't reached max attempts yet
    if (!isLoading && t && !hasTermsTranslations && attemptCount < 3) {
      setAttemptCount(prev => prev + 1);
    }
  }, [t, language, isLoading]); // Removed attemptCount from dependencies to avoid infinite loop

  // Add timeout to prevent infinite loading
  React.useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => {
        if (isLoading) {
          console.log('Terms Component - Loading timeout reached, forcing display');
          setHasTranslations(true);
        }
      }, 3000); // 3 second timeout
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);
  
  // Reset attempt counter when language changes
  React.useEffect(() => {
    setAttemptCount(0);
  }, [language]);

  // Force display after 5 seconds regardless of loading state
  React.useEffect(() => {
    const forceDisplayTimer = setTimeout(() => {
      console.log('Terms Component - Force display timeout reached');
      setHasTranslations(true);
    }, 5000);
    
    return () => clearTimeout(forceDisplayTimer);
  }, []);
  
  // Show loading state if translations are still loading 
  // but with a maximum attempt count to prevent infinite loading
  if ((isLoading || !hasTranslations) && attemptCount < 3) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold mb-4">Loading Terms of Service...</h2>
        <p className="text-gray-500">Please wait while we load the content in {language}...</p>
        {attemptCount > 0 && (
          <p className="mt-4 text-xs text-gray-400">Attempt {attemptCount+1}/3...</p>
        )}
      </div>
    );
  }

  // Fall back to default content if we've exhausted retries or have invalid translations
  // const termsContent = t?.terms || t?.navigations?.terms || {};
  
  const content = (
    <div className="prose max-w-none">
      <h2 className="text-2xl font-semibold mb-4">{t?.terms?.title || t?.navigations?.terms?.title || 'Terms of Service'}</h2>
      
      {/* Last updated */}
      <p className="mb-4 text-gray-500">
        {t?.terms?.lastUpdated || t?.navigations?.terms?.lastUpdated || 'Last Updated: May 3, 2025'}
      </p>

      {/* Introduction */}
      <section className="mb-6">
        <p>{t?.terms?.introduction || t?.navigations?.terms?.introduction || 'These Terms of Service ("Terms") govern your access to and use of the Global24 website. By accessing or using our service, you agree to be bound by these Terms.'}</p>
      </section>

      {/* Acceptance of Terms */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.acceptanceTitle || 'Acceptance of Terms'}</h3>
        <p>{t?.terms?.acceptanceDesc || 'By accessing or using Global24, you agree to be bound by these Terms of Service. If you do not agree to all of these terms, you may not use our services.'}</p>
      </section>

      {/* Changes to Terms */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.changesTitle || 'Changes to Terms'}</h3>
        <p>{t?.terms?.changesDesc || 'We may revise these terms at any time without notice. By continuing to use our website after any changes, you accept the revised terms.'}</p>
      </section>

      {/* User Accounts */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.accountsTitle || t?.navigations?.terms?.accountsTitle || 'User Accounts'}</h3>
        <p>{t?.terms?.accountsDesc || t?.navigations?.terms?.accountsDesc || 'When you create an account with us, you must provide accurate, complete, and current information. You are responsible for safeguarding the password that you use to access our service and for any activities under your account.'}</p>
      </section>

      {/* Content and Intellectual Property */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.contentTitle || 'Content and Intellectual Property'}</h3>
        <p>{t?.terms?.contentDesc || 'All content on our website, including text, graphics, logos, images, and software, is the property of Global24 or its content suppliers and is protected by international copyright laws.'}</p>
      </section>

      {/* User-Generated Content */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.userContentTitle || 'User-Generated Content'}</h3>
        <p>{t?.terms?.userContentDesc || 'By posting content on our site, you grant us a non-exclusive, royalty-free, perpetual, and worldwide license to use, reproduce, modify, and distribute your content in connection with our services.'}</p>
      </section>

      {/* Content and Conduct */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.conductTitle || t?.navigations?.terms?.conductTitle || 'Content and Conduct'}</h3>
        <p>{t?.terms?.conductDesc || t?.navigations?.terms?.conductDesc || 'Our service allows you to post, link, store, share, and otherwise make available certain information, text, graphics, or other material. You are responsible for the content that you post to the service, including its legality, reliability, and appropriateness.'}</p>
      </section>

      {/* Prohibited Uses */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.prohibitedTitle || t?.navigations?.terms?.prohibitedTitle || 'Prohibited Uses'}</h3>
        <p>You agree not to use the service in any way that:</p>
        <ul className="list-disc pl-6">
          {(t?.terms?.prohibitedList && Array.isArray(t.terms.prohibitedList)) ? (
            t.terms.prohibitedList.map((item: string, index: number) => (
              <li key={index}>{item}</li>
            ))
          ) : (t?.navigations?.terms?.prohibitedList && Array.isArray(t.navigations.terms.prohibitedList)) ? (
            t.navigations.terms.prohibitedList.map((item: string, index: number) => (
              <li key={index}>{item}</li>
            ))
          ) : (
            <>
              <li>Violates any law, regulation, or legal right</li>
              <li>Is harmful, fraudulent, deceptive, or offensive</li>
              <li>Attempts to impersonate another person or entity</li>
              <li>Infringes any patent, trademark, trade secret, copyright, or other intellectual rights</li>
              <li>Transmits any viruses, malware, or other malicious code</li>
            </>
          )}
        </ul>
      </section>

      {/* Disclaimer of Warranties */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.disclaimerTitle || 'Disclaimer of Warranties'}</h3>
        <p>{t?.terms?.disclaimerDesc || 'Our website is provided \'as is\' without any warranties, expressed or implied. We do not guarantee that our services will be error-free, secure, or continuously available.'}</p>
      </section>

      {/* Limitation of Liability */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.limitationTitle || 'Limitation of Liability'}</h3>
        <p>{t?.terms?.limitationDesc || 'Global24 shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services.'}</p>
      </section>

      {/* Governing Law */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.governingLawTitle || 'Governing Law'}</h3>
        <p>{t?.terms?.governingLawDesc || 'These Terms shall be governed by the laws of the United States, without regard to its conflict of law provisions.'}</p>
      </section>

      {/* Contact Information */}
      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{t?.terms?.contactTitle || 'Contact Information'}</h3>
        <p>{t?.terms?.contactDesc || 'If you have any questions about these Terms, please contact us at terms@global24.news.'}</p>
      </section>
    </div>
  );

  return (
    <CommonPage
      lang={lang}
      pageType="terms"
      title={t?.terms?.title || t?.navigations?.terms?.title || "Terms of Service"}
      content={content}
    />
  );
};

export default Terms; 